import { PortfolioDto } from '../interfaces/portfolio.dto';

/** The seat looking at the units: an admin runs every one of them. */
export interface UnitViewer {
  investorId: string | undefined;
  isAdmin: boolean;
}

/** What the rules read off a unit in the property list. */
export interface RunnableUnit {
  manager?: { investorId: string } | null;
  portfolio?: { id: string } | null;
  buildingId?: string | null;
}

type PortfolioOwnership = Pick<PortfolioDto, 'id' | 'ownerId'>;

/**
 * The API's rule for changing how a unit is worked, filing it into a portfolio included: an admin,
 * the unit's manager, or the owner of the portfolio it is in. Collaborating on it is not enough.
 */
export function runsUnit(viewer: UnitViewer, unit: RunnableUnit, portfolios: PortfolioOwnership[]): boolean {
  if (viewer.isAdmin) {
    return true;
  }

  if (!viewer.investorId) {
    return false;
  }

  const home = unit.portfolio ? portfolios.find((portfolio) => portfolio.id === unit.portfolio?.id) : undefined;

  return unit.manager?.investorId === viewer.investorId || home?.ownerId === viewer.investorId;
}

/**
 * What a filing dialog offers: into a given portfolio, every unit outside it the viewer runs; with no
 * destination yet, the unassigned ones they run. Any portfolio may take a unit its runner files in.
 */
export function unitsToFile<T extends RunnableUnit>(
  viewer: UnitViewer,
  units: T[],
  portfolios: PortfolioOwnership[],
  destination?: { id: string },
): T[] {
  // A unit in a building is filed where its building is, so it moves with the building, never alone
  return units.filter(
    (unit) =>
      !unit.buildingId &&
      (destination ? unit.portfolio?.id !== destination.id : !unit.portfolio) &&
      runsUnit(viewer, unit, portfolios),
  );
}

/** A new portfolio's owner runs every unit filed into it, so it only takes units its creator runs already. */
export function unitsForNewPortfolio<T extends RunnableUnit>(
  viewer: UnitViewer,
  units: T[],
  portfolios: PortfolioOwnership[],
): T[] {
  return units.filter((unit) => !unit.buildingId && runsUnit(viewer, unit, portfolios));
}

/** An admin makes a portfolio for anyone on the team; a member makes one for themselves. */
export function newPortfolioOwnerId(viewer: UnitViewer, chosenOwnerId: string): string {
  return viewer.isAdmin ? chosenOwnerId : (viewer.investorId ?? '');
}
